import React, { useEffect, useState } from 'react';
import { siteContent } from '../content/siteContent';
import { urlFor } from '../lib/sanity';
import { fetchStaff } from '../lib/staff';

function getStaffImageUrl(photo, size) {
  if (!photo) {
    return null;
  }

  try {
    return urlFor(photo).width(size).height(size).fit('crop').auto('format').url();
  } catch (error) {
    return null;
  }
}

function getStaffAltText(member) {
  if (member?.title) {
    return `Portrait of ${member.name}, ${member.title}`;
  }

  return `Portrait of ${member?.name || 'Langwood tutor'}`;
}

const StaffHighlights = () => {
  const [staffState, setStaffState] = useState({
    staff: [],
    loading: true,
    isFallback: false,
  });
  const { staff: staffContent } = siteContent;

  useEffect(() => {
    let mounted = true;

    async function loadStaff() {
      try {
        const result = await fetchStaff();

        if (!mounted) {
          return;
        }

        setStaffState({
          staff: Array.isArray(result?.staff) ? result.staff : [],
          loading: false,
          isFallback: Boolean(result?.isFallback),
        });
      } catch (error) {
        if (!mounted) {
          return;
        }

        setStaffState({
          staff: [],
          loading: false,
          isFallback: true,
        });
      }
    }

    loadStaff();

    return () => {
      mounted = false;
    };
  }, []);

  const { staff, loading, isFallback } = staffState;

  return (
    <section id="staff" className="staff" aria-labelledby="staff-heading">
      <div className="container">
        <div className="staff-heading">
          <span className="staff-eyebrow">{staffContent.eyebrow}</span>
          <h2 id="staff-heading">{staffContent.heading}</h2>
          <p className="intro">{staffContent.intro}</p>
        </div>

        {loading ? (
          <p className="staff-note" role="status" aria-live="polite">
            Loading staff profiles…
          </p>
        ) : (
          <>
            {isFallback && (
              <p className="staff-note" role="status">
                {staffContent.fallback}
              </p>
            )}

            {staff.length ? (
              <div className="staff-grid card-grid">
                {staff.map((member) => {
                  const src = getStaffImageUrl(member.photo, 800);
                  const srcSet = member.photo
                    ? [320, 480, 640, 800]
                        .map((size) => {
                          const imageUrl = getStaffImageUrl(member.photo, size);
                          return imageUrl ? `${imageUrl} ${size}w` : null;
                        })
                        .filter(Boolean)
                        .join(', ')
                    : undefined;

                  return (
                    <article key={member._id} className={`staff-card surface-card ${member.featured ? 'featured' : ''}`}>
                      <div className="staff-card__media">
                        {src ? (
                          <img
                            src={src}
                            srcSet={srcSet}
                            sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 240px"
                            alt={getStaffAltText(member)}
                            loading="lazy"
                            width="800"
                            height="800"
                          />
                        ) : (
                          <div className="staff-card__placeholder" aria-hidden="true">
                            {member.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <div className="staff-card__copy">
                        <h3>{member.name}</h3>
                        {member.title && <p>{member.title}</p>}
                      </div>
                    </article>
                  );
                })}
              </div>
            ) : (
              <div className="staff-empty surface-card" role="status">
                <p>{staffContent.empty}</p>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default StaffHighlights;
